import React, { useState } from 'react';
import RichTextEditor from '../RichTextEditor';
import playtomicLogo from './assets/playtomic-logo.png';
import './PlayerUpdateForms.css';

/**
 * ShareMatchForm - Form for players to share Playtomic matches
 * Now uses RichTextEditor for formatted thoughts
 */
const ShareMatchForm = ({ onSubmit, onCancel }) => {
  const [playtomicUrl, setPlaytomicUrl] = useState('');
  const [matchThoughts, setMatchThoughts] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [urlError, setUrlError] = useState(null);

  // Playtomic shares links from more than one domain (app.playtomic.io,
  // app.playtomic.com, and country-specific ones) - the backend's own
  // validation (playerUpdateService.js) only ever looks for /matches/{id}
  // and never checks the domain, so this shouldn't be stricter than that.
  const PLAYTOMIC_MATCH_URL = /playtomic\.[a-z.]+\/matches\/[a-f0-9-]+/i;

  const validateUrl = (url) => {
    if (!url) {
      setUrlError('URL is required');
      return false;
    }
    if (!PLAYTOMIC_MATCH_URL.test(url)) {
      setUrlError('Please paste a valid Playtomic match URL');
      return false;
    }
    setUrlError(null);
    return true;
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPlaytomicUrl(url);
    if (url) {
      validateUrl(url);
    }
  };

  // Extract plain text from HTML for validation
  const getPlainText = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.innerText || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateUrl(playtomicUrl)) {
      return;
    }

    const plainText = getPlainText(matchThoughts);
    if (plainText.length > 500) {
      setError('Thoughts must be less than 500 characters');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(playtomicUrl, matchThoughts);
    } catch (err) {
      setError(err.message || 'Failed to share match');
      setSubmitting(false);
    }
  };

  return (
    <form className="player-update-form match-form" onSubmit={handleSubmit}>
      <div className="form-header">
        <h4><img src={playtomicLogo} alt="" className="playtomic-icon" /> Share Match</h4>
        <button
          type="button"
          className="update-form-close"
          onClick={onCancel}
        >
          ✕
        </button>
      </div>

      {error && (
        <div className="form-error">
          ⚠️ {error}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="playtomic-url">Playtomic Match URL</label>
        <input
          id="playtomic-url"
          type="text"
          value={playtomicUrl}
          onChange={handleUrlChange}
          placeholder="Paste your Playtomic match URL here (e.g., https://app.playtomic.io/matches/...)"
          disabled={submitting}
        />
        {urlError && (
          <div className="field-error">
            {urlError}
          </div>
        )}
        <p className="url-hint">
          📋 How to get your match URL:
          <br />
          1. Open Playtomic
          <br />
          2. View your match
          <br />
          3. Copy the URL from your browser address bar
          <br />
          4. Paste it above
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="match-thoughts">Your Thoughts (Optional)</label>
        <RichTextEditor
          value={matchThoughts}
          onChange={setMatchThoughts}
          maxCharacters={500}
          placeholder="Share your reflections on the match (e.g., what went well, what to improve)"
          disabled={submitting}
        />
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn-cancel"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-submit"
          disabled={submitting || !playtomicUrl || !!urlError}
        >
          {submitting ? 'Sharing...' : 'Share Match'}
        </button>
      </div>
    </form>
  );
};

export default ShareMatchForm;
